/* =====================================================================================
   "Waiting on you".

   Four obligations lived in four modules and nobody saw three of them. Each is already
   answered by a database view; the only thing missing was somewhere to put them together.

     read and sign      an approved controlled document requiring acknowledgement
     MLR review         an asset assigned to you in a named capacity
     override conflict  an automated refresh disagreed with a correction you entered
     overdue action     a diary entry past its date
     expense claim      a colleague's claim waiting on your decision

   Deliberately NOT a notification feed. Notifications tell you things happened; this lists
   things that will not resolve until you do something. Anything that cannot be acted on has
   no business here.
   ===================================================================================== */

import { BACKEND } from './db';

export interface InboxItem {
  id: string;
  kind: 'sign' | 'review' | 'conflict' | 'overdue' | 'expense';
  title: string;
  detail: string;
  /** Route to the screen where it can be dealt with. */
  href: string;
  due: string | null;
  overdue: boolean;
}

const live = () => BACKEND === 'supabase';

export async function fetchInbox(): Promise<InboxItem[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const sb = supabase();
  const out: InboxItem[] = [];
  const today = new Date().toISOString().slice(0, 10);

  // Each query is independent and any one may fail (a migration not yet run, a view absent).
  // One failing source must not blank the whole block, so they settle rather than race.
  const [signs, reviews, conflicts, diary, expenses] = await Promise.allSettled([
    sb.schema('quality').from('v_training_register').select('*').eq('outstanding', true),
    sb.schema('studio').from('v_my_reviews').select('*'),
    sb.schema('atlas').from('v_my_override_conflicts').select('*'),
    sb.schema('app').from('diary_entries').select('*').eq('status', 'open').lte('due_date', today),
    sb.schema('ledger').from('v_my_expense_approvals').select('*'),
  ]);

  if (expenses.status === 'fulfilled') {
    for (const r of expenses.value.data ?? []) {
      const amount = new Intl.NumberFormat('en-GB',
        { style: 'currency', currency: r.currency || 'GBP' }).format(Number(r.total ?? 0));
      out.push({
        id: `expense-${r.id}`, kind: 'expense',
        title: `Approve ${amount} from ${r.is_own ? 'your own claim' : r.claimant}`,
        /* The number of departures from policy is on the line, because that is what decides
           whether this is a ten-second approval or a conversation. */
        detail: r.flagged_lines
          ? `${r.reference ?? 'claim'}, ${r.flagged_lines} line(s) departing from the policy`
          : `${r.reference ?? 'claim'}, everything within policy`,
        href: '/expenses',
        due: r.submitted_at ? String(r.submitted_at).slice(0, 10) : null,
        /* Submitted more than a week ago and still undecided. Somebody is out of pocket. */
        overdue: !!r.submitted_at
          && (Date.now() - new Date(r.submitted_at).getTime()) > 7 * 864e5,
      });
    }
  }

  if (signs.status === 'fulfilled') {
    for (const r of signs.value.data ?? []) {
      out.push({
        id: `sign-${r.document_id}`, kind: 'sign',
        title: `Read and sign ${r.doc_number}`,
        detail: r.title, href: '/quality', due: null, overdue: false,
      });
    }
  }
  if (reviews.status === 'fulfilled') {
    for (const r of reviews.value.data ?? []) {
      out.push({
        id: `review-${r.id}`, kind: 'review',
        title: `${r.capacity} review: ${r.job_code}`,
        detail: r.open_blockers > 0
          ? `${r.title}: ${r.open_blockers} open blocker${r.open_blockers > 1 ? 's' : ''}`
          : r.title,
        href: '/studio', due: r.due_on, overdue: Boolean(r.overdue),
      });
    }
  }
  if (conflicts.status === 'fulfilled') {
    for (const r of conflicts.value.data ?? []) {
      out.push({
        id: `conflict-${r.id}`, kind: 'conflict',
        title: `Figure disputed at ${r.site_name}`,
        detail: `You recorded ${r.my_value}; a refresh computed ${r.computed_numeric}. Your value stands until you decide.`,
        href: `/atlas/site/${r.site_id}`, due: null, overdue: false,
      });
    }
  }
  if (diary.status === 'fulfilled') {
    for (const r of diary.value.data ?? []) {
      out.push({
        id: `diary-${r.id}`, kind: 'overdue',
        title: r.title, detail: r.detail ?? '',
        href: '/today', due: r.due_date,
        overdue: new Date(r.due_date) < new Date(today),
      });
    }
  }

  // Overdue first, then anything with a date, then the rest.
  return out.sort((a, b) =>
    Number(b.overdue) - Number(a.overdue)
    || (a.due ?? '9999').localeCompare(b.due ?? '9999'));
}
